import { BottomSheet, type BottomSheetContentRef } from '@s4l/ds'
import Search from 'client/icons/Search'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import Select from 'src/comps/Atom/Select'
import { Text } from 'src/comps/Atom/Text'
import { fetchReviewsRequestAction, setCurrentFilter } from 'src/effects/Reviews/actions'
import { TripReasonFilter } from 'src/effects/Reviews/constants'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { PAGES, SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { useMarket } from 'src/hooks/useMarket'
import { useReviews } from 'src/hooks/useReviews'
import RatingComments from '../RatingComments'
import RatingContent from '../RatingContent'
import RatingSubRatings from '../RatingSubRatings'
import styles from './styles.module.css'

interface IRatingsModalProps {
  isOpen: boolean
  onClose: () => void

  count?: number
  score?: number
  subScores?: Array<{
    name: string
    localizedName: string
    value: number
  }>
  propertyId: number
}

export function RatingsModal(props: Readonly<IRatingsModalProps>) {
  const { formatMessage } = useIntl()

  const { isOpen, onClose, score, subScores, count, propertyId } = props
  const dispatch = useDispatch()
  const { isLoading, pagination, filter, reviewsPaginated } = useReviews()
  const contentRef = React.useRef<BottomSheetContentRef | null>(null)
  const { language } = useMarket()

  const { sendEvent } = useKafkaEvent({
    page: PAGES.DETAIL,
    eventName: 'click_load_more',
  })

  const { sendEvent: sendFilterEvent } = useKafkaEvent({
    page: PAGES.DETAIL,
    eventName: 'select_filter_trip_reason',
  })

  useEffect(() => {
    if (!isLoading) {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.scrollToTop()
        }
      })
    }
  }, [isLoading])

  interface FilterOptions {
    key: TripReasonFilter | 'all'
    value: string
  }

  const filterByTripReason: FilterOptions[] = [
    {
      key: 'all',
      value: formatMessage({ id: 'global.all' }),
    },
    {
      key: 'business',
      value: formatMessage({ id: 'reviews.trip-reason.business' }),
    },
    {
      key: 'leisure',
      value: formatMessage({ id: 'reviews.trip-reason.leisure' }),
    },
    {
      key: 'business_and_leisure',
      value: formatMessage({ id: 'reviews.trip-reason.business-leisure' }),
    },
    {
      key: 'friends_and_family',
      value: formatMessage({ id: 'reviews.trip-reason.friends-family' }),
    },
  ]

  function filterBy(value: FilterOptions) {
    const key = value.key !== 'all' ? value.key : undefined

    dispatch(
      setCurrentFilter({
        filter: key,
      })
    )

    sendFilterEvent(
      { section: SECTIONS.REVIEWS },
      {
        filter: key,
      }
    )

    dispatch(
      fetchReviewsRequestAction({
        propertyId,
        page: 1,
        language,
        tripReason: key,
      })
    )
  }

  function fetchMoreReviews() {
    sendEvent({ section: SECTIONS.REVIEWS })
    dispatch(
      fetchReviewsRequestAction({
        propertyId,
        page: pagination.page + 1,
        language,
        tripReason: filter,
      })
    )
  }

  return (
    <BottomSheet open={isOpen} onOpenChange={onClose}>
      <BottomSheet.Content
        ref={contentRef}
        title={formatMessage({ id: 'global.reviews-from' })}
        modalOnDesktop
        size="l"
        data-testid="ratings-modal"
        scroll
      >
        <div className={styles.ratingsModalContent}>
          <div className={styles.ratings}>
            <RatingContent score={score ?? 0} count={count ?? 0} />
            <RatingSubRatings subScores={subScores ?? []} />
          </div>
          <div>
            <div className={styles.ratingsContainer}>
              <Text as="h4" variant="titleXs" weight="weight3">
                {formatMessage({ id: 'global.filters' })}
              </Text>
              <div className={styles.filters}>
                <Select
                  label={formatMessage({ id: 'reviews.trip-reason' })}
                  defaultValue={filterByTripReason.find(item => item.key === filter)}
                  options={filterByTripReason}
                  onChange={filterBy}
                />
              </div>
              {reviewsPaginated.length > 0 && (
                <RatingComments paginate loadMore={fetchMoreReviews} />
              )}

              {reviewsPaginated.length === 0 && (
                <div className={styles.emptyState}>
                  <Search size={40} />
                  <Text variant="textBodyXl" weight="weight3">
                    {formatMessage({ id: 'global.no-reviews' })}
                  </Text>
                  <Text variant="textBodyL" weight="weight2">
                    {formatMessage({ id: 'global.modify-search-reviews-filters' })}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </BottomSheet.Content>
    </BottomSheet>
  )
}
